<!--
  SectionNav — full-height left drawer for in-page (section) navigation.

  App-style vertical rail: full height under the header, icon + label rows,
  brand-coloured active pill, optional bottom-pinned items, and a collapse
  toggle that shrinks the rail to icons-only.

  Collapse is controlled by the parent (v-model:collapsed) so the host view
  can shrink its content padding in step. The rail width is driven by the
  --section-nav-w CSS variable, which the host also uses for its padding —
  set it once on the page container and both stay in sync.

    <div class="section-layout">
      <SectionNav
        title="Player"
        :items="navItems"
        :active="selectedTab"
        v-model:collapsed="navCollapsed"
        @select="k => tabChange('selectedTab', k)"
      />
      <div class="tabs-wrapper"> ...content panels... </div>
    </div>

  items / bottomItems: [{ key, label, icon }]  (icon = Material Symbols name)
-->
<template>
  <nav class="section-nav" :class="{ 'section-nav--collapsed': collapsed }">
    <div class="section-nav__inner">
      <div class="section-nav__header">
        <span v-if="!collapsed" class="section-nav__title">{{ title }}</span>
        <button
          type="button"
          class="section-nav__toggle"
          :title="collapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="collapsed ? 'Expand menu' : 'Collapse menu'"
          @click="$emit('update:collapsed', !collapsed)"
        >
          <span class="material-symbols-outlined" aria-hidden="true">{{
            collapsed ? "chevron_right" : "chevron_left"
          }}</span>
        </button>
      </div>

      <ul class="section-nav__list">
        <li v-for="item in items" :key="item.key">
          <a
            href="javascript:;"
            :class="['section-nav__link', { active: item.key === active }]"
            :title="collapsed ? item.label : null"
            @click="$emit('select', item.key)"
          >
            <span class="material-symbols-outlined" aria-hidden="true">{{ item.icon }}</span>
            <span v-if="!collapsed" class="section-nav__label">{{ item.label }}</span>
          </a>
        </li>
      </ul>

      <div v-if="bottomItems && bottomItems.length" class="section-nav__bottom">
        <ul class="section-nav__list">
          <li v-for="item in bottomItems" :key="item.key">
            <a
              href="javascript:;"
              :class="['section-nav__link', { active: item.key === active }]"
              :title="collapsed ? item.label : null"
              @click="$emit('select', item.key)"
            >
              <span class="material-symbols-outlined" aria-hidden="true">{{ item.icon }}</span>
              <span v-if="!collapsed" class="section-nav__label">{{ item.label }}</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  name: "SectionNav",
  props: {
    items: {
      type: Array,
      required: true, // [{ key, label, icon }]
    },
    active: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    bottomItems: {
      type: Array,
      default: () => [],
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["select", "update:collapsed"],
};
</script>

<style scoped lang="scss">
$header-h: 5.9rem; // sits just under the fixed app header

/* Full-bleed left rail: fixed under the header, full page height.
   Width is driven by --section-nav-w so the host padding stays in sync. */
.section-nav {
  position: fixed;
  top: $header-h;
  left: 0;
  bottom: 0;
  width: var(--section-nav-w, 260px);
  background: #fff;
  border-right: 1px solid #ececec;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.04);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 40;
  transition: width 0.2s ease;
}

.section-nav__inner {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 10px;
}

/* Header: section title + collapse toggle */
.section-nav__header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 19px 15px 10px;
  margin-bottom: 6px;
  border-bottom: 1px solid #f0f0f0;
}

.section-nav__title {
  flex: 1;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #8a8f98;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.section-nav__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: none;
  border-radius: 8px;
  background: #f5f5f7;
  color: #5a5f68;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  .material-symbols-outlined {
    font-size: 20px;
  }

  &:hover,
  &:focus {
    background: color-mix(in srgb, var(--brand-color) 14%, transparent);
    color: var(--brand-color);
  }
}

.section-nav__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.section-nav__link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  color: #3a3a3a;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.25;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  transition: background 0.15s ease, color 0.15s ease;

  .material-symbols-outlined {
    font-size: 22px;
    flex: 0 0 auto;
    color: inherit;
  }
}

.section-nav__link:hover,
.section-nav__link:focus {
  background: #f5f5f7;
}

.section-nav__link.active {
  background: color-mix(in srgb, var(--brand-color) 12%, transparent);
  color: var(--brand-color);
  font-weight: 600;
}

.section-nav__bottom {
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #ececec;
}

/* Collapsed: icons only, centred */
.section-nav--collapsed {
  .section-nav__header {
    justify-content: center;
    padding: 4px 0 10px;
  }
  .section-nav__link {
    justify-content: center;
    padding: 0.7rem 0;
  }
}

/* Mobile: collapse into a horizontal scrolling bar at the top */
@media (max-width: 768px) {
  .section-nav {
    position: static;
    width: 100%;
    height: auto;
    border-right: none;
    border-bottom: 1px solid #ececec;
    box-shadow: none;
  }
  .section-nav__inner {
    flex-direction: row;
    align-items: center;
    min-height: 0;
    padding: 8px;
    overflow-x: auto;
  }
  .section-nav__header,
  .section-nav__toggle {
    display: none;
  }
  .section-nav__list {
    flex-direction: row;
    flex-wrap: nowrap;
  }
  .section-nav__bottom {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    border-left: 1px solid #ececec;
    padding-left: 8px;
  }
}
</style>
